class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.allPredictiveSearchInstances = document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.areSettingsPassed = this.dataset.settingsPassed === "true" ? true : false;
    this.searchPills = this.querySelectorAll('.predictive-search__suggestions--pills .predictive-search__suggestions--pill')
    this.inputSuggestions = this.querySelector('.search-input-suggestions');
    this.allowInputAnimation = true;
    this.currentAnimationRunning = false;
    this.mobileMenu = document.querySelector('mobile-menu');
    this.detailsElement = this.closest('details');

    this.setupEventListeners();

  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));

    //listen for details open attribute change
    if(this.detailsElement) {
      const observer = new MutationObserver((mutationsList, observer) => {
        for(let mutation of mutationsList) {
          if(mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if(this.detailsElement.hasAttribute('open')) {
              this.open();
            } else {
              this.close();
            }
          }
        }
      });

      observer.observe(this.detailsElement, { attributes: true });
    }



    if(this.searchPills) {
      this.searchPills.forEach((pill) => {
        pill.addEventListener('click', this.onPillClick.bind(this));
      });
    }

    if(this.mobileMenu && window.innerWidth < 990 && this.areSettingsPassed) {
      // listen for class change : mobilemenu--isVisible if it's added animateInputSuggestions
      const observer = new MutationObserver((mutationsList, observer) => {
        for(let mutation of mutationsList) {
          if(mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if(mutation.target.classList.contains('mobilemenu--isVisible')) {
              this.animateInputSuggestions();
            } else {
              this.allowInputAnimation = true;
              this.close();
            }
          }
        }
      });

      observer.observe(this.mobileMenu, { attributes: true });
    }
  }

  animateInputSuggestions() {
    if(!this.allowInputAnimation || this.currentAnimationRunning) return;
    this.allowInputAnimation = false;
    const suggestions = this.inputSuggestions.querySelectorAll('.animated-suggestion');

    this.currentAnimationRunning = true;
    setTimeout(() => {
      this.currentAnimationRunning = false;
    }, suggestions.length * 1250);

    suggestions.forEach((suggestion) => {
      suggestion.style.transform = `translateY(-100%)`;
    });

    suggestions.forEach((suggestion, index) => {
      if(index === 0) {
        suggestion.style.transform = `translateY(0)`;
      }
      if (index != 0) {
        setTimeout(() => {
          suggestion.style.transform = `translateY(0)`;
        }, (index * 1250));
      }
      if(index + 1 !== suggestions.length) {
        setTimeout(() => {
          suggestion.style.transform = `translateY(100%)`;
        }, ((index + 1) * 1250));
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onPillClick(event) {
    event.preventDefault();
    const pill = event.target;
    const pillValue = pill.innerText;
    this.input.value = pillValue;
    this.onChange();
    this.input.focus();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    if (newSearchTerm.length == 0) {
      this.setAttribute("data-show-message", "false");
      if(this.areSettingsPassed) {
        this.setAttribute("data-show-suggestions", true);
      }
    }
    if (newSearchTerm.length >= 1) {
      this.setAttribute("data-show-message", "true");
      if(this.areSettingsPassed) {
        this.setAttribute("data-show-suggestions", false);
      }
    }

    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length && !this.areSettingsPassed) {
      this.close(true);
      return;
    }
    if (this.searchTerm.length > 0) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length && !this.areSettingsPassed) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true" || this.areSettingsPassed) {
      this.open();
    } else if (currentSearchTerm.length) {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length && !this.areSettingsPassed) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector("[data-predictive-search-search-for-text]");
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g"))) {
        if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll("li, button.predictive-search__item")).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, "text/html").querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText = this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(this.querySelector("[data-predictive-search-live-region-count-value]").textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector(".section-header").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    document.body.style.overflow = "hidden";
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    if(this.areSettingsPassed){
      this.resetButton.classList.remove("hidden");
      if(this.searchTerm.length == 0) {
        this.setAttribute("data-show-suggestions", true);
      }
    }
    if(window.innerWidth >= 990) {
      this.animateInputSuggestions();
    }
  }

  close(clearSearchTerm = false) {
    this.allowInputAnimation = true;
    document.body.style.overflow = "";
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if(this.areSettingsPassed){
      this.resetButton.classList.add("hidden");
      this.setAttribute("data-show-suggestions", false);
      // unfocus the input
      this.input.blur();
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
    if(this.areSettingsPassed){
      this.resetButton.classList.add("hidden");
      this.setAttribute("data-show-suggestions", false);
    }
  }
}

customElements.define("predictive-search", PredictiveSearch);
